import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

const OPEN_EVENT = 'quantity-dropdown:open';

/**
 * A custom element that renders a dropdown quantity selector.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} trigger
 * @property {HTMLElement} triggerText
 * @property {HTMLElement} menu
 * @property {HTMLButtonElement[]} options
 * @property {HTMLInputElement} input
 *
 * @extends {Component<Refs>}
 */
class QuantityDropdownComponent extends Component {
  requiredRefs = ['trigger', 'menu'];

  #isOpen = false;
  #justToggled = false;
  #justToggledTimer = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(OPEN_EVENT, this.#handleOtherOpen);
    document.addEventListener('click', this.#handleOutsideClick);
    document.addEventListener('keydown', this.#handleEscape);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(OPEN_EVENT, this.#handleOtherOpen);
    document.removeEventListener('click', this.#handleOutsideClick);
    document.removeEventListener('keydown', this.#handleEscape);
    clearTimeout(this.#justToggledTimer);
  }

  get #options() {
    return this.refs.options ?? [];
  }

  #getCurrentQuantity() {
    const selected = this.#options.find((opt) =>
      opt.classList.contains('quantity-dropdown__option--selected')
    );
    return selected ? parseInt(selected.dataset.value || '1', 10) : 1;
  }

  /**
   * Toggle dropdown open/closed. Wired via `on:click="/toggle"` on the trigger.
   * @param {Event} event
   */
  toggle(event) {
    event.stopPropagation();

    this.#justToggled = true;
    clearTimeout(this.#justToggledTimer);
    this.#justToggledTimer = window.setTimeout(() => {
      this.#justToggled = false;
    }, 100);

    if (this.#isOpen) {
      this.#close(true);
    } else {
      this.#open();
    }
  }

  /**
   * Open dropdown on Enter/Space from the trigger. Wired via `on:keydown="/handleTriggerKeydown"`.
   * @param {KeyboardEvent} event
   */
  handleTriggerKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    if (!this.#isOpen) this.toggle(event);
  }

  /**
   * Select an option on click. Wired via `on:click="/selectOption"` on each option button.
   * @param {Event} event
   */
  selectOption(event) {
    event.stopPropagation();
    const option = event.target instanceof Element
      ? event.target.closest('.quantity-dropdown__option')
      : null;
    if (option instanceof HTMLElement) this.#commitSelection(option);
  }

  /**
   * Handle option keyboard interaction. Wired via `on:keydown="/handleOptionKeydown"`.
   * @param {KeyboardEvent} event
   */
  handleOptionKeydown(event) {
    const option = event.target instanceof Element
      ? event.target.closest('.quantity-dropdown__option')
      : null;
    if (!(option instanceof HTMLElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      this.#commitSelection(option);
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();

      const options = this.#options;
      const currentIndex = options.indexOf(/** @type {HTMLButtonElement} */(option));
      if (currentIndex < 0) return;

      const nextIndex = event.shiftKey
        ? (currentIndex === 0 ? options.length - 1 : currentIndex - 1)
        : (currentIndex === options.length - 1 ? 0 : currentIndex + 1);

      options[nextIndex]?.focus();
    }
  }

  #open() {
    const { menu, trigger } = this.refs;
    const options = this.#options;

    document.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { source: this } }));

    this.#isOpen = true;
    menu.classList.remove('popup--hidden');
    menu.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');

    for (const opt of options) opt.setAttribute('tabindex', '0');

    const currentQty = this.#getCurrentQuantity();
    const focusedIndex = Math.max(0, Math.min(currentQty - 1, options.length - 1));

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        options[focusedIndex]?.focus();
      });
    });
  }

  /**
   * @param {boolean} returnFocus
   */
  #close(returnFocus = false) {
    const { menu, trigger } = this.refs;

    this.#isOpen = false;
    menu.classList.add('popup--hidden');
    menu.setAttribute('aria-hidden', 'true');
    trigger.setAttribute('aria-expanded', 'false');

    for (const opt of this.#options) opt.setAttribute('tabindex', '-1');

    if (returnFocus) trigger.focus();
  }

  /**
   * @param {HTMLElement} option
   */
  #commitSelection(option) {
    const { trigger, triggerText, input } = this.refs;
    const value = parseInt(option.dataset.value || '1', 10);
    const lineIndex = option.dataset.lineIndex ? parseInt(option.dataset.lineIndex, 10) : null;

    for (const opt of this.#options) {
      opt.classList.remove('quantity-dropdown__option--selected');
      opt.setAttribute('aria-selected', 'false');
    }

    option.classList.add('quantity-dropdown__option--selected');
    option.setAttribute('aria-selected', 'true');

    if (triggerText) triggerText.textContent = String(value);
    trigger.setAttribute('aria-label', `Quantity selector, currently ${value}`);

    if (input instanceof HTMLInputElement) input.value = String(value);

    this.#close(true);

    if (lineIndex) {
      document.dispatchEvent(new QuantitySelectorUpdateEvent(value, lineIndex));
    }
  }

  /** @param {Event} event */
  #handleOtherOpen = (event) => {
    if (!(event instanceof CustomEvent)) return;
    if (event.detail?.source !== this && this.#isOpen) this.#close();
  };

  /** @param {Event} event */
  #handleOutsideClick = (event) => {
    if (this.#justToggled || !this.#isOpen) return;
    if (!(event.target instanceof Node)) return;
    if (!this.contains(event.target)) this.#close();
  };

  /** @param {KeyboardEvent} event */
  #handleEscape = (event) => {
    if (event.key !== 'Escape' || !this.#isOpen) return;
    event.preventDefault();
    event.stopPropagation();
    this.#close(true);
  };
}

if (!customElements.get('quantity-dropdown-component')) {
  customElements.define('quantity-dropdown-component', QuantityDropdownComponent);
}
